/**
 * @lume/shared — Política de PROOFING de identidade (Porteiro Neutro A5; ex-"checklist").
 *
 * A 2ª camada do porteiro: como a IA prova a identidade em `self_declared` (contar
 * ≥N fatores DECLARADOS que constam da policy) e quais verticais escalam ações de
 * risco a HUMANO em vez de OTP-por-chat (`riskActionsRequireVerified`).
 *
 * **Renomeado p/ longe de "checklist" (footgun P13):** é PROOFING de identidade,
 * distinto do checklist de tarefa do operador (ADR-045) e do step-governance — só
 * compartilhavam a palavra.
 *
 * **Neutralização (A5):** o MECANISMO (contar) e o PISO (nunca-verified; N≥1) são
 * CÓDIGO; a chave de fator é `string` LIVRE; o "sabor telecom" vive só no SEED
 * (`IDENTITY_PROOFING_POLICIES` — data). Um `resolveProofingPolicy` config-aware
 * (override ▸ seed ▸ default) prepara a autoria por-tenant (casa DB = fatia de
 * promoção deferida); o override é DORMENTE (sem writer) ⇒ byte-idêntico.
 *
 * **PISO inafrouxável (EXATAMENTE dois — Fable A5 Important-2):** (a)
 * `assuranceFromDeclaredFactors` NUNCA retorna `verified` (proofing de chat ≠ fonte
 * forte — só OTP/posse ou ERP eleva); (b) `applyProofingFloor` clampa N a ≥
 * `MIN_FACTORS_FOR_SELF_DECLARED`. **NÃO são piso (config LIVRE, o tenant pode
 * afrouxar):** `riskActionsRequireVerified` (flipar risco de handoff→OTP), a composição
 * de `factors`, e N acima do mínimo. ⇒ o override É capaz de afrouxar ABAIXO do seed
 * nesses eixos (dormente hoje, 0 writers). **★ PRECONDIÇÃO NOMEADA DA PROMOÇÃO:** a
 * casa DB de autoria (fatia futura) DEVE ter um portão de afrouxamento no molde A3
 * `classifyMappingRisk` (rebaixar piso de risco/proofing ⇒ aprovação de plataforma) —
 * o piso estrutural aqui NÃO cobre esses eixos. (Par de I1/I2/M9 da A3.)
 *
 * ★ CONSTRAINT A6 (RESOLVIDA na fatia A6, `access-decision.ts`): o consumidor `applyStepUp`
 * lê `riskActionsRequireVerified` p/ decidir OTP-vs-handoff, com `roleUnrecognized`/
 * `otpResolvable`; A5 preserva esse caminho byte-idêntico. O subset `IdentityGateResult`
 * ganhou `role`+`assuranceOk` e `deriveAccessDecision` separa `needs_identity` de `deny`.
 */
/** Keys usadas pelo SEED (documentação/conveniência — NÃO restringe o mecanismo). */
export const KNOWN_PROOFING_FACTOR_KEYS = [
    'account_holder',
    'cpf_cnpj',
    'full_name',
    'birth_date',
    'email',
    'installation_address',
    'contract_number',
    'membership_card',
    'process_number',
    'debit_confirmation',
];
const F = (key, label) => ({
    key,
    label,
});
/**
 * SEED por vertical (data — o telecom/NetComet é `support`). Verticais de risco
 * (saúde/jurídico/cobrança) nascem CONSERVADORES; varejo/triagem baixa fricção.
 * Ausentes caem no `DEFAULT_IDENTITY_PROOFING_POLICY`. Valores VERBATIM do catálogo
 * anterior (byte-identidade — ver golden differential).
 */
export const IDENTITY_PROOFING_POLICIES = {
    support: {
        vertical: 'support',
        factors: [
            F('account_holder', 'É o titular da conta'),
            F('cpf_cnpj', 'CPF/CNPJ'),
            F('installation_address', 'Endereço de instalação'),
            F('contract_number', 'Número do contrato'),
            F('birth_date', 'Data de nascimento'),
        ],
        requiredForSelfDeclared: 3, // titular + CPF/CNPJ + 1 segundo fator
        proportionality: 'medium',
        riskActionsRequireVerified: false,
        note: 'Telecom (default NetComet): titular + CPF/CNPJ + 2º fator.',
    },
    healthcare: {
        vertical: 'healthcare',
        factors: [
            F('full_name', 'Nome completo'),
            F('birth_date', 'Data de nascimento'),
            F('cpf_cnpj', 'CPF'),
            F('membership_card', 'Carteirinha'),
        ],
        requiredForSelfDeclared: 3,
        proportionality: 'high',
        riskActionsRequireVerified: true, // dado clínico não se valida por chat → escala
        note: 'Saúde: conservador — dado clínico escala; checklist só p/ escopo informativo.',
    },
    legal: {
        vertical: 'legal',
        factors: [
            F('full_name', 'Nome completo'),
            F('cpf_cnpj', 'CPF'),
            F('process_number', 'Número do processo/contrato'),
        ],
        requiredForSelfDeclared: 3,
        proportionality: 'high',
        riskActionsRequireVerified: true, // RISK_VERTICAL (Item 368)
        note: 'Jurídico: adversarial, RISK_VERTICAL — ações de risco exigem verified.',
    },
    collections: {
        vertical: 'collections',
        factors: [
            F('full_name', 'Nome completo'),
            F('cpf_cnpj', 'CPF'),
            F('debit_confirmation', 'Confirmação de dado de débito'),
        ],
        requiredForSelfDeclared: 3,
        proportionality: 'high',
        riskActionsRequireVerified: true, // RISK_VERTICAL (Item 368)
        note: 'Cobrança/Financeiro: RISK_VERTICAL — ações de risco exigem verified.',
    },
    commercial: {
        vertical: 'commercial',
        factors: [F('full_name', 'Nome'), F('email', 'E-mail')],
        requiredForSelfDeclared: 1,
        proportionality: 'low',
        riskActionsRequireVerified: false,
        note: 'Varejo/Comercial: baixa fricção, foco em conversão.',
    },
    // Triage = a PORTA DE ENTRADA (recepção/roteador), não um domínio de negócio
    // delicado. Permite verificação por posse (OTP) p/ LEITURA na entrada; verticais
    // de RISCO reais (saúde/jurídico/cobrança) permanecem conservadores.
    triage: {
        vertical: 'triage',
        factors: [F('full_name', 'Nome completo'), F('cpf_cnpj', 'CPF/CNPJ')],
        requiredForSelfDeclared: 2,
        proportionality: 'low',
        riskActionsRequireVerified: false,
        note: 'Triagem (porta de entrada): permite verificação por posse p/ leitura; não é domínio de risco.',
    },
};
/** Default conservador p/ vertical sem policy própria (ex.: onboarding/other). */
export const DEFAULT_IDENTITY_PROOFING_POLICY = {
    vertical: 'default',
    factors: [F('full_name', 'Nome completo'), F('cpf_cnpj', 'CPF/CNPJ')],
    requiredForSelfDeclared: 2,
    proportionality: 'medium',
    riskActionsRequireVerified: true,
    note: 'Default conservador: vertical sem policy própria exige verified p/ risco.',
};
/** Piso de plataforma p/ o número de fatores: N < 1 auto-elevaria todos. */
export const MIN_FACTORS_FOR_SELF_DECLARED = 1;
/**
 * PISO inafrouxável (CÓDIGO): clampa `requiredForSelfDeclared` a ≥
 * `MIN_FACTORS_FOR_SELF_DECLARED` (N=0 auto-elevaria). INERTE para o seed (todos N ≥ 1)
 * ⇒ byte-idêntico (retorna a MESMA referência). (O outro piso — "nunca-verified" — é
 * intrínseco a `assuranceFromDeclaredFactors`.) Factors vazios já são MÁX. conservadores
 * (self_declared inalcançável) — não caem no DEFAULT (que reintroduziria alcance).
 * NÃO cobre `riskActionsRequireVerified` nem a composição de factors (config livre —
 * ver o docblock do módulo: portão de afrouxamento = precondição da promoção).
 */
export function applyProofingFloor(policy) {
    const flooredN = Math.max(policy.requiredForSelfDeclared, MIN_FACTORS_FOR_SELF_DECLARED);
    if (flooredN === policy.requiredForSelfDeclared)
        return policy;
    return { ...policy, requiredForSelfDeclared: flooredN };
}
/**
 * Resolve a policy de proofing do vertical (CÓDIGO, config-aware). Precedência
 * molde A3: override (tenant/vertical DB — DORMENTE) ▸ seed-por-vertical ▸ DEFAULT;
 * SEMPRE aplica `applyProofingFloor`. Sem `opts` ⇒ resolve o seed, floor inerte ⇒
 * byte-idêntico ao antigo `checklistPolicyForVertical`.
 */
export function resolveProofingPolicy(vertical, opts) {
    const base = opts?.override ??
        (vertical != null && vertical in IDENTITY_PROOFING_POLICIES
            ? IDENTITY_PROOFING_POLICIES[vertical]
            : DEFAULT_IDENTITY_PROOFING_POLICY);
    return applyProofingFloor(base);
}
/**
 * Nível atingido por fatores DECLARADOS pelo cliente, dada a policy. **NUNCA retorna
 * `verified`** (proofing de chat ≠ fonte forte). ≥ `requiredForSelfDeclared` fatores
 * distintos e válidos → `self_declared`; senão `none`. Fatores fora da policy são
 * ignorados.
 */
export function assuranceFromDeclaredFactors(policy, declaredFactorKeys) {
    const allowed = new Set(policy.factors.map((f) => f.key));
    const validCount = new Set(declaredFactorKeys.filter((k) => allowed.has(k))).size;
    return validCount >= policy.requiredForSelfDeclared ? 'self_declared' : 'none';
}
//# sourceMappingURL=proofing-policy.js.map